---
title: 'Tokens, Quality Tiers, and What AI Image Generation Costs'
description: 'Understand how AgentBrush tokens map to quality tiers, what each plan buys, and how to spend tokens strategically to get the most out of every generation.'
slug: ai-image-generation-cost-tokens
category: learning
pillar: A
primaryKeyword: 'AI image generation cost'
audience: all
date: 2026-05-30
updated: 2026-05-30
author: 'AgentBrush Team'
ogImage: /og/blog/ai-image-generation-cost-tokens.png
canonical: ''
schema: Article
featured: false
draft: false
relatedSlugs: [agentbrush-prompting-guide, gpt-image-2-for-ai-agents]
---

**AgentBrush pricing is built around one unit: the token. Tokens map to quality tiers (1, 5, or 20 per image at 1024x1024), and background removal costs 0 tokens because it runs locally.** Understanding that mapping is all you need to plan your budget and pick a plan that fits.

This post covers the full token model, what each plan actually buys in real images, a practical iteration strategy that cuts costs without sacrificing output quality, and three worked cost examples with exact token math.

## The token model: quality tiers and resolution multipliers

Every generation call on AgentBrush resolves to a token cost before it runs. That cost depends on two things: the **quality tier** you pick and the **output size**.

### Quality tiers at 1024x1024

| Quality  | Tokens | What you get                                                                |
| -------- | ------ | --------------------------------------------------------------------------- |
| `low`    | 1      | Rough, fast. Good enough to evaluate composition, palette, and layout.      |
| `medium` | 5      | A solid working draft. Shareable with a stakeholder, not quite print-ready. |
| `high`   | 20     | Full rendering. Use this for your final deliverable.                        |

The gap between `low` and `high` is 20x. That number matters: it defines the entire cost strategy.

### Size multipliers

The square 1024x1024 is the reference price (1.0x). Other sizes scale with pixel area, because that is what they cost to generate (gpt-image-2 bills by resolution):

- **Portrait** (1024x1536): 1.5x the square cost.
- **Landscape** (1536x1024): 1.5x the square cost.

Portrait and landscape pack about 1.5x the pixels of a square, so a `high`-quality one is 30 tokens versus 20 for the square (medium is 8, low is 2). Custom sizes follow the same rule: the multiplier is the canvas area relative to 1024x1024, so a larger image costs proportionally more.

### Background removal

Background removal (`agentbrush_remove_background`) costs **0 tokens**. It runs locally and always outputs a PNG. You can cut out as many drafts as you want without affecting your token balance.

<Compare columns={3} caption="The same scene at 1, 5, and 20 tokens. Low locks the composition for free. High is for the final output only." items={[{ src: 'images/quality-crystal-low.png', alt: 'The same scene at low quality (1 token): an explorer dwarfed in a glowing crystal cavern, rough but the composition and color mood read clearly.', label: 'Low' }, { src: 'images/quality-crystal-medium.png', alt: 'The same scene at medium quality (5 tokens): cleaner, detail starting to resolve, good enough to share with a stakeholder.', label: 'Medium' }, { src: 'images/quality-crystal-high.png', alt: 'The same scene at high quality (20 tokens): full fidelity, god-rays and volumetric atmosphere, the tier you save for the final output.', label: 'High' }]} />

## The plans: what your monthly tokens actually buy

| Plan    | Price/month | Monthly tokens | Quality available   | High images (20 tokens) | Medium images (5 tokens) |
| ------- | ----------- | -------------- | ------------------- | ----------------------- | ------------------------ |
| Starter | $6.99       | 100            | low + medium        | not available           | 20                       |
| Pro     | $14.99      | 600            | low / medium / high | 30                      | 120                      |
| Power   | $29.99      | 1,300          | low / medium / high | 65                      | 260                      |

A few things to note from that table.

**Starter** is the entry point for exploring or occasional work. It runs `low` and `medium` quality, so `high` finals are a Pro feature. 100 tokens is 20 medium drafts or 100 quick `low` checks, which is real work, and it sidesteps the trap of burning a whole plan on five `high` images. Starter is hard-capped, with no overage.

**Pro** unlocks `high` quality and covers a typical solo developer or small team shipping one to two products a month. 600 tokens is roughly 30 high-quality finals plus a full iteration budget, workable for most sprint-sized asset lists. Pro is also hard-capped.

**Power** is for high-volume workflows: game asset pipelines, marketing batch jobs, or agencies generating across multiple clients. It carries 1,300 tokens and is the only tier with overage.

Only Power can exceed its allowance, at $0.04 per token. Starter and Pro stop at their cap until the next cycle, so on those tiers the way to generate more is to move up a tier. The upgrade math is in the section below.

## Draft cheap, commit high

The single most effective cost strategy is also the simplest: **iterate at `low`, then commit one `high`.**

Low quality costs 1 token. High quality costs 20 tokens. If you run five low-quality drafts to lock a composition and then one high-quality final, you have spent 25 tokens total. If you had run those same six generations at `high`, you would have spent 120 tokens. The saving is 95 tokens, or $3.80 at the Power overage rate.

```text
# Step 1: Draft at low (1 token) to check composition and palette
preset: realistic · quality: low
"A lone astronaut on a barren red-rock plateau, enormous ringed planet in the dusty sky, golden late-afternoon light, cinematic, shot on 35mm film"

# Step 2: Tweak one thing (one change per draft)
preset: realistic · quality: low
"A lone astronaut on a barren red-rock plateau, enormous ringed planet in the dusty sky, golden late-afternoon light, cinematic, shot on 35mm film, shallow depth of field, dust haze in foreground"

# Step 3: Commit when the draft looks right (20 tokens)
preset: realistic · quality: high
"A lone astronaut on a barren red-rock plateau, enormous ringed planet in the dusty sky, golden late-afternoon light, cinematic, shot on 35mm film, shallow depth of field, dust haze in foreground, no watermark, no text"
```

The principle behind single-change follow-ups is worth naming explicitly. If you change four things at once and the result improves, you do not know which change did it. Change one thing per iteration. When the draft locks, stop.

For anything that needs a transparent background, remember that removal is free. Cut out every draft iteration without touching your token balance.

## Worked cost examples

### Example 1: 10-sprite game set

You are building a top-down indie game and need ten distinct enemy sprites in a consistent pixel-art style. Each sprite needs one low-quality layout check and one high-quality final.

| Step          | Quality | Tokens each | Count | Subtotal |
| ------------- | ------- | ----------- | ----- | -------- |
| Layout drafts | low     | 1           | 10    | 10       |
| Final renders | high    | 20          | 10    | 200      |

**Total: 210 tokens.**

On Starter (100 tokens): not an option here. Starter runs low and medium only, and the ten high-quality finals alone are 200 tokens. You can draft on Starter, but the finals need Pro.

On Pro (600 tokens): comfortably inside the plan. 210 of your 600 tokens used.

On Power (1,300 tokens): leaves you 1,090 tokens for the rest of the month.

If you want to add background removal for all ten finals, that is 0 additional tokens.

### Example 2: product shoot (studio + 3 lifestyle + 1 cut-out)

You are launching a product and need: one clean studio shot (square), three lifestyle scenes (landscape, 30 tokens each at `high`), and one transparent-background cut-out.

| Asset                        | Quality | Size             | Tokens |
| ---------------------------- | ------- | ---------------- | ------ |
| Studio shot draft            | low     | 1024x1024        | 1      |
| Studio shot final            | high    | 1024x1024        | 20     |
| Lifestyle scene 1 final      | high    | landscape (1.5x) | 30     |
| Lifestyle scene 2 final      | high    | landscape        | 30     |
| Lifestyle scene 3 final      | high    | landscape        | 30     |
| Background removal           | local   | PNG              | 0      |
| Iteration drafts (say 3 low) | low     | 1024x1024        | 3      |

**Total: 114 tokens.**

On Starter: not available, since this shoot is built on high-quality finals and Starter stops at medium. On Pro: 114 of 600 tokens, leaving plenty of budget for more rounds or follow-up scenes.

### Example 3: 10-image marketing batch

You are generating a social content batch: ten portrait-format marketing images (1.5x the square cost at `high`, so 30 tokens each). Each gets one low draft and one high final.

| Step          | Quality         | Tokens each | Count | Subtotal |
| ------------- | --------------- | ----------- | ----- | -------- |
| Layout drafts | low (portrait)  | 2           | 10    | 20       |
| Final renders | high (portrait) | 30          | 10    | 300      |

**Total: 320 tokens.**

On Starter: not available. The batch is high-quality, which Starter does not include.

On Pro: 320 of 600 tokens. A single batch fits comfortably; two in one month (640 tokens) just clear the 600 cap, which is the nudge to Power.

On Power: two batches (640 tokens) plus a sprite set (210 tokens) is 850, comfortably inside the 1,300 month.

## Caps, overage, and when to upgrade

Only **Power** has overage, billed at **$0.04 per token** beyond its 1,300-token allowance. **Starter and Pro are hard-capped:** when you hit the limit, generation pauses until the next cycle, so the way to keep going is to move up a tier. That cap keeps the bill predictable and makes the upgrade call a simple one.

How to think about each step:

- **Starter to Pro:** two reasons to move up, headroom (100 tokens to 600) and quality (Starter is low and medium only; Pro unlocks `high`). If you need a high-quality final, or you keep hitting 100, Pro is the step.
- **Pro to Power:** once you regularly bump the 600-token ceiling, Power roughly doubles the price ($14.99 to $29.99) for more than double the tokens (600 to 1,300), and it adds overage so you are never cut off mid-run. A heavy Pro month that keeps stalling at the cap is the signal.

Beyond Power's 1,300, overage at $0.04 per token keeps you generating without interruption. There is no rollover of unused tokens to the next month, so an allocation is a monthly budget, not a running balance.

## Where this breaks down

**The size multipliers track pixel area.** Portrait and landscape are about 1.5x the square; treat the exact token counts as close estimates, not contractual rates, and confirm against your own balance.

**Iteration counts are unpredictable.** A straightforward product shot might lock in two low-quality drafts. A complex scene with a specific stylistic target might take eight. Budget conservatively for projects with high visual requirements.

**Low quality is not always enough to judge the result.** For very detailed subjects (a dense isometric city, a tight portrait with specific facial features), low quality sometimes omits enough detail that it cannot tell you whether the final will work. Run a medium draft in those cases before committing to high.

## FAQ

**Do tokens expire?**
Yes. Your plan allocation is per calendar month and does not roll over. On Power, tokens used beyond the 1,300 allowance are billed at $0.04 each; Starter and Pro stop at their cap.

**Is there a free tier?**
There is no free tier at the moment. The Starter plan at $6.99/month is the entry point.

**Does quality affect generation speed?**
Yes, higher quality takes longer to render. Low quality is the fastest path when you are iterating. For automated pipelines where you are generating a large batch, this is worth accounting for in your runtime estimates.

**Can I mix quality tiers in the same project?**
Yes, and you should. The standard workflow is low for iteration, high for finals. Nothing prevents you from generating some assets at medium for stakeholder review while keeping finals at high. Each call is independent.

---

For a practical look at prompt structure that maximizes what you get from each token, see [Prompt AgentBrush Like a Pro](/blog/agentbrush-prompting-guide). For the model behind the generations, see [what gpt-image-2 means for image generation inside your AI agent](/blog/gpt-image-2-for-ai-agents).

**Ready to start?** [Connect AgentBrush to your agent](/getting-started) and run your first generation at `quality: low`. One token to see if the composition is right.
